// Khung xếp hạng & bộ sưu tập (spec 17/10/2026). Ba cửa /cosmetics/*.
import { useContext, useMemo } from "react";
import {
  QueryClient,
  QueryClientContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";

export type EquippedRow = components["schemas"]["CosmeticsEquippedRow"];
export type Collection = components["schemas"]["CosmeticsCollection"];
export type CollectionItem = components["schemas"]["CosmeticsCollectionItem"];
export type EquipInput = components["schemas"]["CosmeticsEquipInput"];

// Khung là thành phần LÁ ở ~90 màn (đi theo StaffAvatar): cây nào không có
// QueryClientProvider thì không gọi mạng, coi như không ai đeo gì — y luật của
// danh bạ ảnh (`lib/staff-avatar.ts`), không được làm sập màn chính.
const idleClient = new QueryClient();
const EMPTY = new Map<string, EquippedRow>();
const mapCache = new WeakMap<object, Map<string, EquippedRow>>();

/** Ai đang đeo gì — tải MỘT lần cho cả app, giữ 5 phút. Lỗi/đang tải ⇒ rỗng. */
export function useEquippedCosmetics(): Map<string, EquippedRow> {
  const provided = useContext(QueryClientContext);
  const q = useQuery(
    {
      queryKey: ["cosmetics", "equipped"],
      queryFn: async () => {
        const { data, error } = await api.GET("/cosmetics/equipped", {});
        if (error || !Array.isArray(data)) throw new Error("Failed to load cosmetics");
        return data;
      },
      staleTime: 300_000,
      retry: 1,
      enabled: provided !== undefined,
    },
    provided ?? idleClient,
  );
  return useMemo(() => {
    const rows = q.data;
    if (!Array.isArray(rows)) return EMPTY;
    const cached = mapCache.get(rows);
    if (cached) return cached;
    const m = new Map<string, EquippedRow>();
    for (const r of rows as unknown[]) {
      // Thân lạ (bộ giả của bài kiểm khác, máy chủ lỗi) ⇒ bỏ qua dòng đó.
      const row = r as Partial<EquippedRow> | null;
      if (!row || typeof row.staffId !== "string") continue;
      const f = row.frame as Partial<NonNullable<EquippedRow["frame"]>> | null | undefined;
      const t = row.title as Partial<NonNullable<EquippedRow["title"]>> | null | undefined;
      m.set(row.staffId, {
        staffId: row.staffId,
        frame: f && typeof f.code === "string" && typeof f.tier === "number" && typeof f.group === "string"
          ? (f as NonNullable<EquippedRow["frame"]>)
          : null,
        title: t && typeof t.label === "string" && t.label ? (t as NonNullable<EquippedRow["title"]>) : null,
        style: typeof row.style === "string" ? row.style : "game",
        craftTier:
          row.craftTier && typeof row.craftTier.grade === "number" && row.craftTier.grade > 0 ? row.craftTier : null,
      });
    }
    mapCache.set(rows, m);
    return m;
  }, [q.data]);
}

export function useEquippedFor(staffId?: string | null): EquippedRow | undefined {
  const m = useEquippedCosmetics();
  return staffId ? m.get(staffId) : undefined;
}

export function useMyCollection() {
  return useQuery({
    queryKey: ["cosmetics", "me"],
    queryFn: async () => {
      const { data, error } = await api.GET("/cosmetics/me", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được bộ sưu tập"));
      return data;
    },
  });
}

export function useEquip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EquipInput) => {
      const { data, error } = await api.PUT("/cosmetics/me/equip", { body });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đổi được món đang đeo"));
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["cosmetics", "me"], data);
      void qc.invalidateQueries({ queryKey: ["cosmetics", "equipped"] });
    },
  });
}
